import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {InputFileAcceptMimes, InputFileAcceptTypes, UploadStates} from "../../../../../core/constants/enums";
import {readFile, ReadingFileAs} from "../../../../../core/services/utils/utils";
import Utils from "../../../../../core/services/utils/utils";
import Api from "../../../../../core/services/api/api";
import {Col, Row} from "reactstrap";
import AddWarrantyAndReturnDocumentationDropZone from "../../dropzone/add-part-support-request-doc";
import EditImageDialog from "../../../dialogs/edit-image";


export const UpsertPartSupportRequestDocumentFileTypes = {
    everyFormat: 1,
    pdf: 2,
    compressed: 3,
    video: 4,
    image: 5,
}

const UpsertPartSupportRequestDocument = ({
                                              doc,
                                              setDocuments,
                                              setAlreadyDefinedDocuments,
                                              alreadyDefinedDocuments,
                                              requestId,
                                              userId,
                                              customerId,
                                              warehouseCode,
                                          }) => {
    const [uploading, setUploading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [uploadState, setUploadState] = useState({});
    const [editImage, setEditImage] = useState({open: false, file: null, url: null});
    const [tempUrl, setTempUrl] = useState();
    const isMounted = useIsMounted();

    /**
     * Tracks the progress of the uploading image, and updates the status based on it.
     * @param progressEvent {ProgressEvent}
     */
    const onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadState({
            state: UploadStates.pending,
            percentage: percentCompleted
        });
    }

    /**
     * Based on the type of the doc:
     * - if the selected file is an image, then opens the edit image dialog so that the user can edit the selected
     * file to be uploaded
     * - otherwise, just uploads the file
     * @param files {File[] | null}
     */
    const onUploadFile = async (files) => {
        const data = files[0];
        if (doc.validFileTypeId === UpsertPartSupportRequestDocumentFileTypes.image || new RegExp(InputFileAcceptMimes.images).test(data.type)) {
            return setEditImage({file: data, open: true});
        }
        await uploadFile({
            buffer: await readFile(data, ReadingFileAs.binaryString),
            dataUrl: await readFile(data, ReadingFileAs.dataUrl),
            name: data.name,
        })
    }

    /**
     * Uploads the selected content for the selected document. If already exists, replaces the content of the doc
     * @param buffer
     * @param dataUrl
     * @param name
     * @return {Promise<void>}
     */
    const uploadFile = useCallback(async ({buffer, dataUrl, name}) => {
        setTempUrl(dataUrl);
        setUploading(true);
        const forApi = {
            templateId: doc.id,
            partSupportReqId: requestId,
            fileName: Utils.sanitizeFileName(name),
            fileContent: btoa(buffer),
            userId: userId ?? null,
            customerId: customerId ?? null,
            warehouseCode: warehouseCode ?? null,
        }
        const response = await Api.uploadPartSupportTemplate(forApi, onUploadProgress)
        if (!isMounted()) return;
        if (!response?.resultFlag) {
            setUploadState({
                state: UploadStates.error,
                percentage: 0,
            })
            setUploading(false);
            return;
        }
        setTempUrl(null);
        setUploading(false);
        setUploadState({
            state: UploadStates.done,
            percentage: 100,
        })
        setDocuments(prevState => [...prevState.map(e => e.id === doc.id ? {
            ...e,
            url: response.data?.file?.url,
            selectedTypeId: response?.data?.typeAndExtension?.id,
            fileName: name,
        } : e)])
        const found = alreadyDefinedDocuments.find(e => e.partSupportReqTemplateId === doc.id);
        if (found) {
            setAlreadyDefinedDocuments(prevState => [...prevState.map(e => e.id === doc.id
                ? response.data
                : e
            )])
        } else {
            setAlreadyDefinedDocuments(prevState => [...prevState, response.data])
        }
    }, [doc.id, warehouseCode, userId, customerId, requestId, isMounted, setDocuments, setAlreadyDefinedDocuments, alreadyDefinedDocuments])

    /**
     * Removes a submitted template for partSupportRequest.
     * @type {function(): Promise<void>}
     */
    const removeImage = useCallback(async () => {
        setRemoving(true);
        const forApi = {
            partSupportReqId: requestId,
            templateId: doc.id,
            userId: userId ?? null,
            customerId: customerId ?? null,
            warehouseCode: warehouseCode ?? null,
        }
        const response = await Api.removePartSupportTemplate(forApi);
        if (!isMounted())
            return
        if (response?.resultFlag) {
            setDocuments(prevState => [...prevState.map(e => e.id === doc.id ? {
                ...e,
                url: null,
                tempUrl: null,
                fileName: null,
                selectedTypeId: null,
            } : e)])
            setAlreadyDefinedDocuments(prevState => prevState.filter(e => e.partSupportReqTemplateId === doc.id))
            setUploadState({})
        }
        setRemoving(false);
    }, [requestId, warehouseCode, doc.id, userId, customerId, isMounted, setDocuments, setAlreadyDefinedDocuments])

    /**
     * Gets the accepted type formats of the
     * @return {*}
     */
    const getAcceptedFormats = useMemo(() => {
        let extensions = doc?.validFileType?.extensions?.map(e => `.${e.title}`) ?? []
        if (!extensions?.length) {
            extensions = [
                InputFileAcceptMimes.images,
                InputFileAcceptTypes.heic,
                InputFileAcceptTypes.heif,
                ...InputFileAcceptMimes.videos.split(','),
                InputFileAcceptTypes.pdf,
                InputFileAcceptTypes.zip,
                InputFileAcceptTypes.rar,
            ]
        }
        return extensions;
    }, [doc?.validFileType?.extensions])


    return (
        <>
            <Col xs={12} sm={6} xl={4} className='p-2 d-flex flex-column'>
                <Row className={'no-gutters w-100 justify-content-center align-items-start flex-grow-1'}>
                    <Col xs={1}>
                        <div className={'rounded-circle background-red mr-2 mt-2'}
                             style={{width: '7px', height: '7px'}}/>
                    </Col>
                    <Col className={'flex-grow-1'}>
                        <p className={'text-color-1 text-sm text-left text-wrap'}>
                            {doc.title ?? "--"}
                        </p>
                        <p className={'text-xs font-weight-semi-bold text-color-1'}>
                            {doc?.isRequired ? "* Required" : ""}
                        </p>
                    </Col>
                </Row>
                <div className={'w-100 my-1'}/>
                <AddWarrantyAndReturnDocumentationDropZone
                    disabled={uploading || removing}
                    uploading={uploading}
                    onSuccess={onUploadFile}
                    onRemove={removeImage}
                    doc={doc}
                    accept={getAcceptedFormats}
                    tempUrl={tempUrl}
                    setTempUrl={setTempUrl}
                    uploadState={uploadState}
                    onEdit={() => setEditImage({open: true, url: doc.url})}
                />
            </Col>
            <EditImageDialog
                file={editImage.file}
                url={editImage.url}
                open={editImage.open}
                edit={!!editImage.url}
                setOpen={() => setEditImage({file: null, open: false, url: null})}
                onFinished={uploadFile}
            />
        </>

    );
}


export default UpsertPartSupportRequestDocument;
